import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Search, 
  Link2,
  Package,
  ShoppingCart,
  CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reconciliation = () => {
  const [orphanData, setOrphanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrphan, setSelectedOrphan] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [associating, setAssociating] = useState(false);

  useEffect(() => {
    fetchOrphanSales();
  }, []);

  const fetchOrphanSales = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/sales/orphan-products`);
      setOrphanData(res.data);
    } catch (error) {
      console.error("Error fetching orphan sales:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleAssociate = async (productEan) => {
    if (!selectedOrphan) return;

    setAssociating(true);
    try {
      const res = await axios.post(`${API}/sales/associate-product`, {
        orphan_ean: selectedOrphan.ean,
        product_ean: productEan
      });

      toast.success("Associação realizada!", {
        description: `${res.data.sales_updated} vendas atualizadas para ${res.data.product_name}`
      });

      setSelectedOrphan(null);
      setSearchQuery("");
      setSearchResults([]);
      fetchOrphanSales();
    } catch (error) {
      toast.error("Erro ao associar", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    } finally {
      setAssociating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-8 animate-fadeIn" data-testid="reconciliation-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Reconciliação</h1>
        <p className="text-muted-foreground">Vendas de produtos sem entrada no estoque</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Produtos Órfãos</p>
            <p className="kpi-value text-amber-600">{orphanData?.total_orphan_eans || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">EANs sem correspondência</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Qtd Vendida</p>
            <p className="kpi-value">{orphanData?.total_orphan_qty || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">unidades sem estoque</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground mb-1">Receita Órfã</p>
            <p className="kpi-value text-2xl">{formatCurrency(orphanData?.total_orphan_revenue)}</p>
            <p className="text-xs text-muted-foreground mt-1">sem controle de estoque</p>
          </CardContent>
        </Card>
        <Card className="kpi-card bg-green-50 border-green-200">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-700">Dica</p>
            </div>
            <p className="text-xs text-green-700">
              Associe os produtos órfãos aos produtos do estoque para corrigir o controle
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Orphan Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
            Vendas sem Entrada no Estoque ({orphanData?.orphan_products?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orphanData?.orphan_products?.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="data-table" data-testid="orphan-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th>EAN</th>
                    <th>Nome (da venda)</th>
                    <th>Qtd Vendida</th>
                    <th>Receita</th>
                    <th>Vendas</th>
                    <th>Período</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanData.orphan_products.map((orphan) => (
                    <tr key={orphan.ean} className="bg-amber-50/50">
                      <td className="font-mono text-xs">{orphan.ean}</td>
                      <td className="max-w-[200px] truncate" title={orphan.name}>
                        {orphan.name}
                      </td>
                      <td className="font-mono font-bold text-amber-700">{orphan.total_qty}</td>
                      <td className="font-mono">{formatCurrency(orphan.total_revenue)}</td>
                      <td className="font-mono">{orphan.sales_count}</td>
                      <td className="text-xs">
                        {formatDate(orphan.first_sale)} - {formatDate(orphan.last_sale)}
                      </td>
                      <td>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedOrphan(orphan);
                            setSearchQuery("");
                            setSearchResults([]);
                          }}
                          data-testid={`associate-${orphan.ean}`}
                        >
                          <Link2 className="w-4 h-4 mr-1" />
                          Associar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
              <p className="text-green-700">Nenhuma venda órfã encontrada!</p>
              <p className="text-sm">Todos os produtos vendidos têm correspondência no estoque</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Associate Dialog */}
      <Dialog open={!!selectedOrphan} onOpenChange={() => setSelectedOrphan(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Associar Produto
            </DialogTitle>
          </DialogHeader>

          {selectedOrphan && (
            <div className="space-y-4">
              {/* Orphan Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <p className="text-sm font-medium text-amber-800 mb-2">Produto da Venda (sem estoque)</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">EAN:</span>
                    <span className="font-mono ml-2">{selectedOrphan.ean}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Qtd Vendida:</span>
                    <span className="font-mono ml-2">{selectedOrphan.total_qty}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Nome:</span>
                    <span className="ml-2">{selectedOrphan.name}</span>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Buscar produto no estoque para associar:
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Digite o nome ou EAN do produto..."
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border rounded-sm max-h-[250px] overflow-y-auto">
                  {searchResults.map((product) => (
                    <div 
                      key={product.id}
                      className="p-3 border-b last:border-0 hover:bg-muted/50 cursor-pointer flex items-center justify-between"
                      onClick={() => handleAssociate(product.ean)}
                    >
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          EAN: <span className="font-mono">{product.ean}</span>
                          <span className="mx-2">•</span>
                          Estoque: <span className="font-mono">{product.quantity}</span>
                          <span className="mx-2">•</span>
                          Custo: {formatCurrency(product.cost_price)}
                        </p>
                      </div>
                      <Button size="sm" disabled={associating}>
                        {associating ? "..." : "Selecionar"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                <div className="text-center py-4 text-muted-foreground">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum produto encontrado</p>
                </div>
              )}

              {searching && (
                <div className="text-center py-4 text-muted-foreground">
                  Buscando...
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedOrphan(null)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reconciliation;
